import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Settings,
  Menu,
  ChevronLeft,
  LogOut,
  HelpCircle,
  Tag,
  AlertTriangle,
  User,
  ShieldCheck,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useOuvidoriaStore } from "@/stores/ouvidoriaStore";
import { useAuth } from "@/contexts/AuthContext";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificacoesPopover } from "@/components/notifications/NotificacoesPopover";
import { WelcomeModal } from "@/components/onboarding/WelcomeModal";
import { OnboardingTutorial } from "@/components/onboarding/OnboardingTutorial";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { useCurrentAgenteStore } from "@/stores/currentAgenteStore";
import { useCurrentOuvidoriaStore } from "@/stores/currentOuvidoriaStore";
import { useImageBlobUrl } from "@/lib/media";
import { podeSupervisionar } from "@/lib/ouvidoria";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import { AgenteReprovadoScreen } from "@/components/auth/AgenteReprovadoScreen";
import { autenticacaoService } from "@/services/autenticacaoService";
import { useToast } from "@/hooks/use-toast";
import logoUrbnet from "@/assets/images/UrbNetLogoSemFundo.png";

type NavItem = {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  gestorOnly?: boolean;
};

// Regra do backend: PodeSupervisionar = Ativa && Aprovada && (Geral || AssociacaoSupervisora).
// A supervisora tem supervisora própria (a Geral) e supervisionadas — exibe ambos os itens.
const buildNavigation = (opts: {
  isGeral: boolean;
  podeSupervisionar: boolean;
}): NavItem[] => [
  { name: "Visão Geral", href: "/dashboard", icon: LayoutDashboard },
  { name: "Solicitações", href: "/solicitacoes", icon: FileText },
  { name: "Agentes", href: "/agentes", icon: Users },
  { name: "Ouvidoria", href: "/ouvidoria", icon: Building2, gestorOnly: true },
  ...(opts.podeSupervisionar
    ? [{ name: "Supervisionadas", href: "/ouvidorias-supervisionadas", icon: Building2 }]
    : []),
  ...(!opts.isGeral
    ? [{ name: "Supervisora", href: "/supervisora", icon: Building2 }]
    : []),
  { name: "Tópicos", href: "/topicos", icon: Tag },
  { name: "Gráficos", href: "/graficos", icon: BarChart3 },
  // { name: "Validações", href: "/validacoes", icon: ShieldCheck },
  // { name: "Relatórios", href: "/relatorios", icon: BarChart3 },
];

const bottomNavigation: NavItem[] = [
  { name: "Configurações", href: "/configuracoes", icon: Settings },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOuvidoria, logout } = useOuvidoriaStore();
  const { isGestorOuvidoria } = useAuth();
  const { isGestor: isGestorByPapel } = useAgentePermissions();
  const { ouvidoria: currentOuvidoriaData, fetchCurrentOuvidoria } = useCurrentOuvidoriaStore();
  
  // Verifica se o agente é gestor pelo papel ou pelo role do AuthContext
  const isGestor = isGestorOuvidoria() || isGestorByPapel;

  // Default: trata como geral até carregar (evita flicker do menu "Supervisora")
  const isGeralOuvidoria = currentOuvidoriaData?.geral ?? true;

  const navigation = buildNavigation({
    isGeral: isGeralOuvidoria,
    podeSupervisionar: podeSupervisionar({
      geral: isGeralOuvidoria,
      instituicao: currentOuvidoriaData?.instituicao,
      status: currentOuvidoriaData?.status,
      arquivada: currentOuvidoriaData?.arquivada === true,
    }),
  });
  const visibleNavigation = navigation.filter(
    (item) => !item.gestorOnly || isGestor
  );

  // Fecha o drawer ao navegar
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Bloqueia scroll do body quando o drawer está aberto
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Onboarding state
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [welcomeName, setWelcomeName] = useState("Agente");

  useEffect(() => {
    if (searchParams.get("welcome") === "true") {
      setWelcomeName(searchParams.get("name") || "Agente");
      setShowWelcome(true);
      // Clean up URL params
      searchParams.delete("welcome");
      searchParams.delete("name");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleStartTutorial = () => {
    setShowWelcome(false);
    setShowTutorial(true);  
  };

  const handleSkipTutorial = () => {
    setShowWelcome(false);
  };

  const handleFinishTutorial = () => {
    setShowTutorial(false);
    navigate("/");
  };

  const handleLogout = () => {
    logout();
    autenticacaoService.removerToken();
    navigate('/login');
  };

  const { toast } = useToast();

  // Carrega o agente logado (foto + nome + status) via store global
  const fetchCurrentAgente = useCurrentAgenteStore((s) => s.fetchCurrentAgente);
  const { agente, isReadOnly, isAguardandoAprovacao, status: agenteStatus } = useAgentePermissions();
  const isReprovado = agenteStatus === "Reprovado";

  useEffect(() => {
    fetchCurrentAgente(true);
    fetchCurrentOuvidoria();
  }, [fetchCurrentAgente, fetchCurrentOuvidoria]);

  const agenteNome = agente?.nome || "Agente";
  const agenteFoto = agente?.fotoUrl || "";
  const agenteFotoBlob = useImageBlobUrl(agenteFoto || undefined);

  // Dados da ouvidoria para header (nome minimal) — agora vem do store global
  const ouvidoriaNome = currentOuvidoriaData?.nome || currentOuvidoriaData?.nomeFantasia || "Ouvidoria Municipal";
  const userName = agenteNome;
  const userInitials = (agenteNome || "AG");

  
  // Se o agente foi reprovado, bloqueia totalmente o acesso ao sistema
  // e exibe apenas a tela de explicação.
  if (isReprovado) {
    return (
      <AgenteReprovadoScreen
        nomeAgente={agente?.nome}
        nomeOuvidoria={ouvidoriaNome}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="min-h-screen flex w-full bg-transparent">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen bg-sidebar transition-all duration-300 ease-in-out",
          // Desktop: largura depende de collapsed
          "lg:translate-x-0",
          collapsed ? "lg:w-16" : "lg:w-64",
          // Mobile: sempre 64 de largura, abre/fecha via translate
          "w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-center px-4 border-b border-sidebar-border">
          <img 
            src={logoUrbnet} 
            alt="UrbNet Logo" 
            className={cn(
              "object-contain transition-all duration-300 h-10",
              collapsed && "lg:w-10"
            )}
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          {visibleNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Tooltip key={item.name} delayDuration={0}>
                <TooltipTrigger asChild>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-sidebar-accent text-sidebar-primary"
                        : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                    )}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className={cn(collapsed && "lg:hidden")}>
                      {item.name}
                    </span>
                  </Link>
                </TooltipTrigger>
                {collapsed && (
                  <TooltipContent
                    side="right"
                    className="font-body hidden lg:block"
                  >
                    {item.name}
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="absolute bottom-0 left-0 right-0 p-2 border-t border-sidebar-border space-y-1">
          {/* Perfil do agente */}
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Link
                to="/perfil"
                className={cn(
                  "flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  location.pathname === "/perfil"
                    ? "bg-sidebar-accent text-sidebar-primary"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                )}
              >
                <Avatar className="h-7 w-7 flex-shrink-0">
                  {agenteFotoBlob ? (
                    <AvatarImage
                      key={agenteFotoBlob}
                      src={agenteFotoBlob}
                      alt={agenteNome}
                      onError={(e) => {
                        console.error("[DashboardLayout sidebar] AvatarImage error:", e.currentTarget.src, e);
                      }}
                      onLoad={() => {
                        console.log("[DashboardLayout sidebar] AvatarImage loaded:", agenteFotoBlob);
                      }}
                    />
                  ) : null}
                  <AvatarFallback className="text-xs">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <span className={cn("truncate", collapsed && "lg:hidden")}>
                  {agenteNome}
                </span>
              </Link>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent
                side="right"
                className="font-body hidden lg:block"
              >
                {agenteNome}
              </TooltipContent>
            )}
          </Tooltip>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Link
                to="/configuracoes"
                className={cn(
                  "flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  location.pathname === "/configuracoes"
                    ? "bg-sidebar-accent text-sidebar-primary"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                )}
              >
                <Settings className="h-5 w-5 flex-shrink-0" />
                <span className={cn(collapsed && "lg:hidden")}>Configurações</span>
              </Link>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent
                side="right"
                className="font-body hidden lg:block"
              >
                Configurações
              </TooltipContent>
            )}
          </Tooltip>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button 
                onClick={() => navigate('/reportar-problema')}
                className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
              >
                <HelpCircle className="h-5 w-5 flex-shrink-0" />
                <span className={cn(collapsed && "lg:hidden")}>Ajuda</span>
              </button>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent
                side="right"
                className="font-body hidden lg:block"
              >
                Reportar problema
              </TooltipContent>
            )}
          </Tooltip>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button 
                onClick={handleLogout}
                className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-destructive/80 hover:text-destructive hover:bg-destructive/10 transition-colors"
              >
                <LogOut className="h-5 w-5 flex-shrink-0" />
                <span className={cn(collapsed && "lg:hidden")}>Sair</span>
              </button>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent
                side="right"
                className="font-body hidden lg:block"
              >
                Sair
              </TooltipContent>
            )}
          </Tooltip>
        </div>

        {/* Collapse Button (apenas desktop) */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 z-50 hidden lg:flex h-6 w-6 items-center justify-center rounded-full bg-card border border-border shadow-sm hover:bg-surface-container transition-colors"
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              collapsed && "rotate-180"
            )}
          />
        </button>
      </aside>

      {/* Main Content */}
      <div
        className={cn(
          "flex-1 transition-all duration-300 min-w-0",
          // Mobile: sem margem (sidebar é overlay). Desktop: margem conforme collapse.
          collapsed ? "lg:ml-16" : "lg:ml-64",
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-card/80 backdrop-blur-sm border-b border-border">
          <div className="flex h-full items-center justify-between px-4 sm:px-6 gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden flex-shrink-0"
                onClick={() => setMobileOpen(true)}
                aria-label="Abrir menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div className="min-w-0">
                <h1 className="font-heading text-base sm:text-lg font-semibold text-foreground truncate">
                  {navigation.find((n) => n.href === location.pathname)?.name ||
                    "Dashboard"}
                </h1>
                <p className="text-caption truncate">
                  {ouvidoriaNome}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <NotificacoesPopover />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/reportar-problema?origem=${encodeURIComponent(location.pathname)}`)}
                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-950/20"
                aria-label="Reportar um problema"
              >
                <AlertTriangle className="h-5 w-5" />
              </Button>
              <div className="h-8 w-px bg-border mx-1 sm:mx-2 hidden sm:block" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 sm:gap-3 rounded-lg p-1 hover:bg-surface-container transition-colors"
                    aria-label="Abrir menu de usuário"
                  >
                    <div className="text-right hidden md:block">
                      <p className="text-sm font-medium">{userName}</p>
                      <p className="text-caption">{isGestor ? 'Gestor' : 'Agente'}</p>
                    </div>
                    <Avatar className="h-9 w-9 flex-shrink-0">
                      {agenteFotoBlob ? (
                        <AvatarImage
                          key={agenteFotoBlob}
                          src={agenteFotoBlob}
                          alt={userName}
                          onError={(e) => {
                            console.error("[DashboardLayout header] AvatarImage error:", e.currentTarget.src, e);
                          }}
                          onLoad={() => {
                            console.log("[DashboardLayout header] AvatarImage loaded:", agenteFotoBlob);
                          }}
                        />
                      ) : null}
                      <AvatarFallback className="bg-primary/10 text-primary font-heading font-semibold text-sm">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate("/perfil")}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Meu Perfil</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sair</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Read-only banner para agentes aguardando aprovação */}
        {isReadOnly && (
          <div className="border-b border-warning/30 bg-warning/10 text-warning-foreground">
            <div className="flex items-start gap-3 px-4 sm:px-6 py-3">
              <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-warning">
                  {isAguardandoAprovacao
                    ? "Sua conta está aguardando aprovação"
                    : "Sua conta está com acesso restrito"}
                </p>
                <p className="text-muted-foreground">
                  Você pode visualizar os dados, mas ações de edição, aprovação,
                  reprovação, atribuição, arquivamento e alteração de status
                  estão desabilitadas até que sua conta seja aprovada.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 sm:p-6">{children}</main>
      </div>

      {/* Onboarding */}
      <WelcomeModal
        open={showWelcome}
        onStart={handleStartTutorial}
        onSkip={handleSkipTutorial}
        agentName={welcomeName}
      />
      <OnboardingTutorial
        active={showTutorial}
        onFinish={handleFinishTutorial}
        isGestor={isGestor}
      />

    </div>
  );
}
